/* ==========================================================================
   LUXE Stock Status - delivery-date estimator + variant-change sync
   --------------------------------------------------------------------------
   - Progressive enhancement: the snippet renders an honest "N-M working days"
     fallback server-side; this turns it into a friendly date estimate.
   - Business-day maths in the region's timezone, skipping weekends + UK bank
     holidays (snapshot below - refresh ~yearly from https://www.gov.uk/bank-holidays.json).
   - Re-computes on variant change (mirrors the luxe_price pubsub hook).
   Vanilla JS, no dependencies, no layout shift (height is reserved in CSS).
   ========================================================================== */
(function () {
  'use strict';

  /* Idempotency guard: this file is enqueued on the PDP and globally (header,
     for the cart drawer strip). Run the IIFE once. */
  if (window.LuxeDelivery) return;

  /* England & Wales bank holidays (gov.uk snapshot). UK timezone only.
     Update yearly. A missing future holiday only makes an estimate slightly
     optimistic; weekend-skipping always applies. */
  var UK_HOLIDAYS = {
    '2026-01-01': 1, '2026-04-03': 1, '2026-04-06': 1, '2026-05-04': 1,
    '2026-05-25': 1, '2026-08-31': 1, '2026-12-25': 1, '2026-12-28': 1,
    '2027-01-01': 1, '2027-03-26': 1, '2027-03-29': 1, '2027-05-03': 1,
    '2027-05-31': 1, '2027-08-30': 1, '2027-12-27': 1, '2027-12-28': 1
  };

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function iso(date) {
    return date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());
  }

  /* Current civil date + hour in a given IANA timezone. */
  function nowParts(tz) {
    var parts = {};
    try {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, year: 'numeric', month: '2-digit',
        day: '2-digit', hour: '2-digit', hour12: false
      }).formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    } catch (e) {
      var d = new Date();
      return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(), hour: d.getHours() };
    }
    return { y: +parts.year, m: +parts.month, d: +parts.day, hour: +parts.hour };
  }

  function isBusinessDay(date, holidaysApply) {
    var dow = date.getUTCDay();
    if (dow === 0 || dow === 6) return false;
    if (holidaysApply && UK_HOLIDAYS[iso(date)]) return false;
    return true;
  }

  function nextBusinessDay(date, holidaysApply) {
    var d = new Date(date.getTime());
    do { d.setUTCDate(d.getUTCDate() + 1); } while (!isBusinessDay(d, holidaysApply));
    return d;
  }

  function addBusinessDays(date, n, holidaysApply) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < n) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (isBusinessDay(d, holidaysApply)) added++;
    }
    return d;
  }

  function fmtParts(date, locale) {
    var f = {};
    try {
      new Intl.DateTimeFormat(locale || 'en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
      }).formatToParts(date).forEach(function (p) { f[p.type] = p.value; });
    } catch (e) {
      new Intl.DateTimeFormat('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
      }).formatToParts(date).forEach(function (p) { f[p.type] = p.value; });
    }
    return f; /* {weekday, day, month} */
  }

  function compute(el) {
    if (!el) return;
    var min = parseInt(el.getAttribute('data-arrival-min'), 10);
    var max = parseInt(el.getAttribute('data-arrival-max'), 10);
    var cutoff = parseInt(el.getAttribute('data-cutoff-hour'), 10);
    var tz = el.getAttribute('data-tz') || 'Europe/London';
    var prefix = el.getAttribute('data-prefix') || 'Estimated delivery';
    var format = el.getAttribute('data-format') || 'date_range';
    var locale = el.getAttribute('data-locale') || 'en-GB';

    if (isNaN(min) || isNaN(max) || format === 'working_days') return; /* keep server fallback */
    if (isNaN(cutoff)) cutoff = 15;

    var holidaysApply = (tz === 'Europe/London');
    var now = nowParts(tz);
    var hr = now.hour % 24; /* guard against some engines returning 24 at midnight */
    var start = new Date(Date.UTC(now.y, now.m - 1, now.d, 12)); /* noon UTC avoids DST edges */

    /* After cutoff, or on a weekend/holiday, the order is processed the next business day. */
    if (hr >= cutoff || !isBusinessDay(start, holidaysApply)) {
      start = nextBusinessDay(start, holidaysApply);
    }

    var earliest = addBusinessDays(start, min, holidaysApply);
    var latest = addBusinessDays(start, max, holidaysApply);

    var text;
    if (format === 'by_date') {
      var L = fmtParts(latest, locale);
      text = prefix + ' by ' + L.weekday + ' ' + L.day + ' ' + L.month;
    } else { /* date_range */
      var e = fmtParts(earliest, locale);
      var l = fmtParts(latest, locale);
      if (earliest.getTime() === latest.getTime()) {
        text = prefix + ' ' + l.weekday + ' ' + l.day + ' ' + l.month;
      } else if (e.month === l.month) {
        /* copy rule (frozen 2026-07-07): date ranges use "to", never a dash */
        text = prefix + ' ' + e.weekday + ' ' + e.day + ' to ' + l.weekday + ' ' + l.day + ' ' + l.month;
      } else {
        text = prefix + ' ' + e.weekday + ' ' + e.day + ' ' + e.month + ' to ' + l.weekday + ' ' + l.day + ' ' + l.month;
      }
    }
    el.textContent = text;
  }

  function computeWithin(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-luxe-delivery]');
    for (var i = 0; i < nodes.length; i++) compute(nodes[i]);
  }

  /* Expose the engine so other surfaces (e.g. the cart drawer strip) can
     re-compute their own delivery nodes after an AJAX re-render, without
     duplicating the business-day maths. */
  window.LuxeDelivery = { computeWithin: computeWithin, compute: compute };

  function init() {
    computeWithin(document);

    /* Re-render on variant change (same mechanism as luxe_price). */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var data = event && event.data;
        if (!data || !data.html || !data.sectionId) return;
        var id = 'luxe-stock-status-' + data.sectionId;
        var fresh = data.html.getElementById(id);
        var current = document.getElementById(id);
        if (fresh && current) {
          current.replaceWith(fresh);
          compute(document.getElementById(id).querySelector('[data-luxe-delivery]'));
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
